import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Pill-style tabs. Switches panels with the full WAI-ARIA Tabs keyboard
 * pattern (Arrow/Home/End, roving tabindex). Host markup is rendered by
 * blocks/tabs.liquid + blocks/_tab.liquid; this only enhances behavior.
 *
 * Pre-JS, CSS shows only the first panel. On connect we set the host's
 * `data-ready` flag and drive panel visibility via the `hidden` attribute.
 *
 * @extends Component<{ tab: HTMLButtonElement[]; panel: HTMLElement[] }>
 */
class TabsComponent extends Component {
  // No required refs: an empty Tabs (zero _tab children) must upgrade without throwing;
  // the length guard + array-safe getters handle the empty case.
  requiredRefs = [];

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    // A parent block can't read child block settings, so the pill labels are
    // rendered empty server-side; each panel carries its label in
    // `data-tab-label`. Copy it into the matching button here.
    this.#fillLabels();

    const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#activate(selected < 0 ? 0 : selected, { focus: false, scroll: false });

    this.#controller = new AbortController();
    this.addEventListener('keydown', this.#onKeydown, { signal: this.#controller.signal });

    this.dataset.ready = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    const { tab } = this.refs;
    return Array.isArray(tab) ? tab : tab ? [tab] : [];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    const { panel } = this.refs;
    return Array.isArray(panel) ? panel : panel ? [panel] : [];
  }

  /** Copy each panel's `data-tab-label` into its controlling tab button. */
  #fillLabels() {
    const panels = this.#panels;
    for (const tab of this.#tabs) {
      const panel = panels.find((p) => p.id === tab.getAttribute('aria-controls'));
      const label = panel?.dataset.tabLabel;
      if (label == null) continue;
      const span = tab.querySelector('.tabs__tab-label') ?? tab;
      span.textContent = label;
    }
  }

  /**
   * Bound declaratively via `on:click="/select"` on each tab button (the
   * leading slash is required: it tells the base Component the value is a
   * method on the closest component, not a CSS selector).
   * @param {Event} event
   */
  select = (event) => {
    const target = /** @type {Element} */ (event.target);
    const tab = target.closest('[role="tab"]');
    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index < 0) return;
    this.#activate(index);
  };

  /**
   * @param {number} index
   * @param {{ focus?: boolean; scroll?: boolean }} [options]
   */
  #activate(index, { focus = true, scroll = true } = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const activeClass = this.dataset.activeScheme;

    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle('tabs__tab--active', isActive);
      if (activeClass) tab.classList.toggle(activeClass, isActive);
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    const activeTab = tabs[index];
    if (focus) activeTab?.focus();
    if (scroll && activeTab) {
      activeTab.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        inline: 'nearest',
        block: 'nearest',
      });
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current < 0) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next);
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
